import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface GuardProps {
  children: React.ReactNode;
}

export const PrivateRoute: React.FC<GuardProps> = ({ children }) => {
  const { status, session, profileChecked } = useAuth();
  const location = useLocation();

  const isStudentCheckingProfile = status === 'authenticated' && String(session?.user?.role).toUpperCase() === 'STUDENT' && !profileChecked;

  if (status === 'loading' || isStudentCheckingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-on-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">
            {status === 'loading' ? "Authenticating session..." : "Checking profile status..."}
          </p>
        </div>
      </div>
    );
  }

  if (status !== 'authenticated' || !session?.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

interface RoleGuardProps extends GuardProps {
  allowedRoles: string[];
}

export const RoleGuard: React.FC<RoleGuardProps> = ({ children, allowedRoles }) => {
  const { session, status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-on-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (status !== 'authenticated' || !session?.user) {
    return <Navigate to="/login" replace />;
  }

  const role = session.user.role || 'STUDENT';
  if (!allowedRoles.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Expose standard AdminRoute helper for simpler imports
export const AdminRoute: React.FC<GuardProps> = ({ children }) => {
  return <RoleGuard allowedRoles={['ADMIN']}>{children}</RoleGuard>;
};
